/**
 * Dashboards — Step 50.
 *
 * Kai: *"They should also be customisable, so you can see whatever you
 * want."*
 *
 * A widget is a saved VIEW plus a way of drawing it. That is the whole design
 * and it is why this page is short: the filtering, the sharing and the
 * `@me` / `@myGroups` resolution are all already solved by views, so a
 * dashboard is an arrangement of them rather than a second query language.
 * It also means a card can never quietly disagree with the list it claims to
 * count — clicking one opens exactly those rows.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { LayoutDashboard, Megaphone, Pencil, Plus, Share2, Trash2, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Modal } from '../components/ui/modal';
import { apiFetch, apiSend } from '../lib/api';
import { qk } from '../lib/queryClient';
import { useDirty } from '../lib/dirty';
import { useUrlState } from '../lib/urlState';
import { useViews } from '../features/tickets/views';
import { ticketsApi } from '../features/tickets/api';
import { useOrgRealtime } from '../features/tickets/useOrgRealtime';
import { WidgetCard, WIDGET_COLOURS, type Widget } from '../features/dashboards/WidgetCard';
import { cn } from '../lib/utils';
import { useConfirm } from '../lib/confirm';

interface Dashboard {
  id: string;
  name: string;
  scope: string;
  groupId: string | null;
  widgets: Widget[] | null;
  isDefault: number;
}

const KINDS = [
  { value: 'scorecard', label: 'Number' },
  { value: 'bar', label: 'Breakdown' },
] as const;
const GROUP_BYS = [
  { value: 'status', label: 'Status' },
  { value: 'priority', label: 'Priority' },
  { value: 'type', label: 'Type' },
  { value: 'group', label: 'Group' },
  { value: 'assignee', label: 'Agent' },
];

export function DashboardPage() {
  const confirm = useConfirm();
  const qc = useQueryClient();
  const navigate = useNavigate();
  /**
   * Step 60 — the dashboard DOES refresh itself.
   *
   * Nothing here can move under a cursor: these are counts and charts, and a
   * scorecard quietly showing yesterday's number is the failure this exists
   * to fix. The QUEUE is the opposite case and deliberately waits to be
   * asked — see `useOrgRealtime`.
   */
  useOrgRealtime({ auto: true, invalidate: [qk.dashboards.all] });
  const views = useViews();
  const [selected, setSelected] = useUrlState('d', '');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Widget[] | null>(null);
  const [adding, setAdding] = useState(false);
  /** Which widget the modal is editing — null when it is adding a new one.
   *  Kai: *"I also can't edit existing widgets"*. One modal for both, because
   *  a separate editor would be the same eight fields drifting apart. */
  const [editingId, setEditingId] = useState<string | null>(null);
  /** The settings modal — name and who can see it. */
  const [settings, setSettings] = useState(false);
  const [form, setForm] = useState({ name: '', scope: 'private', groupId: '' });
  const [neu, setNeu] = useState<Widget>({ id: '', kind: 'scorecard', title: '', viewId: null, span: 1 });
  /**
   * Has the title been written by hand?
   *
   * Picking a view fills the title in — the view's name is nearly always what
   * the card should say, and typing it again is a tax. But it must never
   * overwrite a title somebody has actually written, so once they type it is
   * theirs. Clearing the box hands it back, which is what people expect from
   * a field that filled itself in.
   */
  const [titleTouched, setTitleTouched] = useState(false);

  const list = useQuery({
    queryKey: qk.dashboards.all,
    queryFn: () => apiFetch<{ entries: Dashboard[] }>('/api/dashboards'),
  });
  const boards = list.data?.entries ?? [];
  const current = boards.find((b) => b.id === selected) ?? boards.find((b) => b.isDefault) ?? boards[0];

  const widgets = draft ?? current?.widgets ?? [];
  const dirty = editing && draft !== null;
  const clearDirty = useDirty(dirty);

  useEffect(() => {
    if (!editing) setDraft(null);
  }, [editing]);

  const save = useMutation({
    mutationFn: () => apiSend(`/api/dashboards/${current!.id}`, 'PATCH', { widgets: draft ?? [] }),
    onSuccess: (r) => {
      if (!r.ok) return toast.error(r.error ?? 'Could not save.');
      clearDirty();
      setDraft(null);
      setEditing(false);
      void qc.invalidateQueries({ queryKey: qk.dashboards.all });
    },
  });
  const groups = useQuery({ queryKey: qk.orgGroups, queryFn: ticketsApi.groups, staleTime: 5 * 60_000 });

  const saveSettings = useMutation({
    mutationFn: () =>
      apiSend(`/api/dashboards/${current!.id}`, 'PATCH', {
        name: form.name.trim(),
        scope: form.scope,
        groupId: form.scope === 'group' ? form.groupId || null : null,
      }),
    onSuccess: (r) => {
      if (!r.ok) return toast.error(r.error ?? 'Could not save.');
      setSettings(false);
      void qc.invalidateQueries({ queryKey: qk.dashboards.all });
    },
  });

  const create = useMutation({
    mutationFn: (name: string) => apiSend<{ id: string }>('/api/dashboards', 'POST', { name, widgets: [] }),
    onSuccess: (r) => {
      if (!r.ok || !r.body) return toast.error(r.error ?? 'Could not create.');
      setSelected(r.body.id);
      void qc.invalidateQueries({ queryKey: qk.dashboards.all });
    },
  });
  const destroy = useMutation({
    mutationFn: (id: string) => apiSend(`/api/dashboards/${id}`, 'DELETE'),
    onSuccess: () => {
      setSelected('');
      void qc.invalidateQueries({ queryKey: qk.dashboards.all });
    },
  });

  const viewOptions = useMemo(() => views.map((v) => ({ value: v.id, label: v.name })), [views]);

  if (list.isPending)
    return (
      <div className="space-y-3 p-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
      </div>
    );

  if (!boards.length)
    return (
      <div className="p-4">
        <Card>
          <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
            <LayoutDashboard className="h-7 w-7 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No dashboards yet.</p>
            <Button onClick={() => create.mutate('My dashboard')}>
              <Plus className="h-4 w-4" />
              Create one
            </Button>
          </CardContent>
        </Card>
      </div>
    );

  return (
    <div className="space-y-4 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={current?.id ?? ''} onChange={(e) => setSelected(e.target.value)} className="w-56">
          {boards.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </Select>
        {current && (
          <span className="rounded border px-1.5 py-0.5 text-xs text-muted-foreground">
            {current.scope === 'private' ? 'Just me' : current.scope === 'group' ? 'Group' : 'Everyone'}
          </span>
        )}
        <div className="flex-1" />
        {/* Step 51 — Kai wanted announcements reachable without going through
            Create, and the dashboard is the first thing people land on. */}
        <Button size="sm" variant="outline" onClick={() => navigate('/announcements')}>
          <Megaphone className="h-4 w-4" />
          Announcements
        </Button>
        {editing ? (
          <>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setEditingId(null);
                setNeu({ id: '', kind: 'scorecard', title: '', viewId: null, span: 1 });
                setTitleTouched(false);
                setAdding(true);
              }}
            >
              <Plus className="h-4 w-4" />
              Add widget
            </Button>
            <Button size="sm" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                clearDirty();
                setEditing(false);
              }}
            >
              Done
            </Button>
          </>
        ) : (
          <>
            <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
              <Pencil className="h-4 w-4" />
              Edit
            </Button>
            {current && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setForm({
                    name: current.name,
                    scope: current.scope,
                    groupId: current.groupId ?? '',
                  });
                  setSettings(true);
                }}
              >
                <Share2 className="h-4 w-4" />
                Share
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => create.mutate('New dashboard')}>
              <Plus className="h-4 w-4" />
              New
            </Button>
            {current && (
              <Button
                size="sm"
                variant="ghost"
                onClick={async () => {
                  if (
                    await confirm({
                      title: `Delete “${current.name}”?`,
                      destructive: true,
                      confirmLabel: 'Delete',
                    })
                  )
                    destroy.mutate(current.id);
                }}
                aria-label="Delete dashboard"
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            )}
          </>
        )}
      </div>

      {widgets.length === 0 && (
        <Card>
          <CardContent className="p-10 text-center text-sm text-muted-foreground">
            {editing ? 'Add a widget to get started.' : 'This dashboard is empty. Edit it to add widgets.'}
          </CardContent>
        </Card>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {widgets.map((w) => (
          <div
            key={w.id}
            className={cn('relative', (w.span ?? 1) > 1 && 'sm:col-span-2', editing && 'cursor-pointer')}
            onClick={
              editing
                ? () => {
                    setNeu(w);
                    setTitleTouched(true);
                    setEditingId(w.id);
                    setAdding(true);
                  }
                : undefined
            }
          >
            {editing && (
              <button
                type="button"
                onClick={() => setDraft(widgets.filter((x) => x.id !== w.id))}
                className="absolute right-1 top-1 z-10 rounded p-1 text-muted-foreground hover:bg-accent hover:text-destructive"
                aria-label={`Remove ${w.title}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
            <WidgetCard
              w={w}
              {...(editing
                ? {
                    onEdit: () => {
                      setNeu(w);
                      setTitleTouched(true);
                      setEditingId(w.id);
                      setAdding(true);
                    },
                  }
                : {})}
            />
          </div>
        ))}
      </div>

      {/* Personal and shared dashboards (Kai). The same three scopes as saved
          views, deliberately — a second vocabulary for "who can see this"
          would mean explaining a difference that does not exist. */}
      <Modal open={settings} onClose={() => setSettings(false)} title="Dashboard settings">
        <form
          className="space-y-3 p-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (form.name.trim()) saveSettings.mutate();
          }}
        >
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Name</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Who can see it</label>
            <Select value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })}>
              <option value="private">Just me</option>
              <option value="group">A group</option>
              <option value="org">Everyone on the service desk</option>
            </Select>
          </div>
          {form.scope === 'group' && (
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Group</label>
              <Select value={form.groupId} onChange={(e) => setForm({ ...form, groupId: e.target.value })}>
                <option value="">Choose a group…</option>
                {(groups.data?.entries ?? []).map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </Select>
              {!form.groupId && (
                <p className="mt-1 text-xs text-muted-foreground">
                  With no group chosen, only you will see it.
                </p>
              )}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setSettings(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                !form.name.trim() ||
                saveSettings.isPending ||
                // Guarded on DIRTINESS as well as validity — an enabled Save on
                // an untouched form is a button that promises work it will not
                // do. `audit-ui[save-state]` caught this.
                (form.name === current?.name &&
                  form.scope === current?.scope &&
                  form.groupId === (current?.groupId ?? ''))
              }
            >
              {saveSettings.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        open={adding}
        onClose={() => {
          setAdding(false);
          setEditingId(null);
        }}
        title={editingId ? 'Edit widget' : 'Add a widget'}
      >
        <form
          className="space-y-3 p-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!neu.title.trim() || !neu.viewId) return;
            setDraft(
              editingId
                ? widgets.map((x) => (x.id === editingId ? { ...neu, id: editingId } : x))
                : [...widgets, { ...neu, id: crypto.randomUUID() }],
            );
            setAdding(false);
            setEditingId(null);
            setNeu({ id: '', kind: 'scorecard', title: '', viewId: null, span: 1 });
            setTitleTouched(false);
          }}
        >
          {/* The view comes FIRST because it names the card: a widget is a
              view plus a way of drawing it, and nothing else can be counted
              here on purpose — one definition of "which tickets", so the card
              and the list can never disagree. */}
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Counts this view</label>
            <Select
              value={neu.viewId ?? ''}
              autoFocus
              onChange={(e) => {
                const viewId = e.target.value || null;
                const name = viewOptions.find((v) => v.value === viewId)?.label ?? '';
                setNeu((n) => ({ ...n, viewId, ...(titleTouched ? {} : { title: name }) }));
              }}
            >
              <option value="">Choose a view…</option>
              {viewOptions.map((v) => (
                <option key={v.value} value={v.value}>
                  {v.label}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Title</label>
            <Input
              value={neu.title}
              onChange={(e) => {
                setNeu({ ...neu, title: e.target.value });
                // Emptying it hands the title back to the view.
                setTitleTouched(e.target.value.trim() !== '');
              }}
            />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Show as</label>
              <Select
                value={neu.kind}
                onChange={(e) => setNeu({ ...neu, kind: e.target.value as Widget['kind'] })}
              >
                {KINDS.map((k) => (
                  <option key={k.value} value={k.value}>
                    {k.label}
                  </option>
                ))}
              </Select>
            </div>
            {neu.kind !== 'scorecard' && (
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">Broken down by</label>
                <Select
                  value={neu.groupBy ?? 'status'}
                  onChange={(e) => setNeu({ ...neu, groupBy: e.target.value })}
                >
                  {GROUP_BYS.map((g) => (
                    <option key={g.value} value={g.value}>
                      {g.label}
                    </option>
                  ))}
                </Select>
              </div>
            )}
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Colour</label>
            <Select
              value={neu.colour ?? ''}
              onChange={(e) => setNeu({ ...neu, colour: e.target.value || null })}
            >
              {WIDGET_COLOURS.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </Select>
            <p className="mt-1 text-xs text-muted-foreground">
              A breach overrides this — amber past the warning number, red past the critical one.
            </p>
          </div>
          {neu.kind === 'scorecard' && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">Warn at</label>
                <Input
                  type="number"
                  value={neu.warnAt ?? ''}
                  onChange={(e) => setNeu({ ...neu, warnAt: e.target.value ? Number(e.target.value) : null })}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">Critical at</label>
                <Input
                  type="number"
                  value={neu.criticalAt ?? ''}
                  onChange={(e) =>
                    setNeu({ ...neu, criticalAt: e.target.value ? Number(e.target.value) : null })
                  }
                />
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setAdding(false);
                setEditingId(null);
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                !neu.title.trim() ||
                !neu.viewId ||
                // Dirty as well as valid: when EDITING, an enabled Save on an
                // untouched form promises work it will not do
                // (`audit-ui[save-state]`).
                (!!editingId &&
                  JSON.stringify(neu) === JSON.stringify(widgets.find((x) => x.id === editingId)))
              }
            >
              {editingId ? 'Save widget' : 'Add'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
